import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Loader2, X, Calendar, MessageSquare, Mail, Phone } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const FollowUps = () => {
  const [followups, setFollowups] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const [formData, setFormData] = useState({
    lead_id: '',
    follow_up_date: '',
    follow_up_time: '',
    follow_up_type: 'call',
    remarks: '',
    next_follow_up_date: '',
    assigned_to: '',
    reminder_type: 'email',
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const [followupsRes, leadsRes] = await Promise.all([
        api.get('/followups', { params: filterStatus ? { status: filterStatus } : {} }),
        api.get('/leads'),
      ]);
      setFollowups(followupsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      showError('Failed to fetch follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/followups', formData);
      showSuccess('Follow-up scheduled successfully');
      setShowModal(false);
      setFormData({ lead_id: '', follow_up_date: '', follow_up_time: '', follow_up_type: 'call', remarks: '', next_follow_up_date: '', assigned_to: '', reminder_type: 'email' });
      fetchData();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to create follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const getLeadName = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    return lead ? lead.customer_name : 'Unknown';
  };

  const getReminderIcon = (type) => {
    switch (type) {
      case 'email': return <Mail size={16} />;
      case 'whatsapp':
      case 'sms': return <MessageSquare size={16} />;
      case 'call': return <Phone size={16} />;
      case 'visit': return <Calendar size={16} />;
      default: return <Mail size={16} />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Follow-ups</h1>
            <p className="text-muted-foreground">Schedule and manage customer follow-ups</p>
          </div>
          <button data-testid="add-followup-button" onClick={() => setShowModal(true)} className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors">
            <Plus size={20} />
            New Follow-up
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <select data-testid="filter-followup-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
            <option value="">All Follow-ups</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
          </select>
        </div>

        <div className="space-y-4">
          {followups.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
              No follow-ups scheduled yet
            </div>
          ) : (
            followups.map((f) => (
              <div key={f.id} data-testid={`followup-${f.id}`} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-primary/10 flex-shrink-0">
                    <Calendar className="text-primary" size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
                      <div>
                        <h3 className="font-bold text-foreground">
                          {f.customer_name || getLeadName(f.lead_id)}
                          {f.lead_number && <span className="ml-2 font-mono text-xs text-muted-foreground">{f.lead_number}</span>}
                        </h3>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        f.status === 'completed' ? 'bg-green-100 text-green-800' :
                        f.status === 'missed' ? 'bg-red-100 text-red-800' :
                        f.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {f.status}
                      </span>
                    </div>
                    {(f.remarks || f.notes) && (
                      <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">{f.remarks || f.notes}</p>
                    )}
                    <div className="flex items-center gap-x-4 gap-y-1 text-xs flex-wrap text-foreground/80">
                      <span className="flex items-center gap-1">
                        <Calendar size={13} />
                        {f.follow_up_date}{f.follow_up_time ? ` at ${f.follow_up_time}` : ''}
                      </span>
                      {f.follow_up_type && (
                        <span className="flex items-center gap-1 capitalize">
                          {getReminderIcon(f.follow_up_type)} {f.follow_up_type}
                        </span>
                      )}
                      {f.assigned_to_name && (
                        <span>Assigned to <span className="font-medium text-foreground">{f.assigned_to_name}</span></span>
                      )}
                      {f.branch_name && <span>· {f.branch_name}</span>}
                      {f.next_follow_up_date && (
                        <span>· Next: <span className="font-medium">{f.next_follow_up_date}</span></span>
                      )}
                    </div>
                    {f.status === 'pending' && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                          data-testid={`followup-complete-${f.id}`}
                          onClick={async () => { try { await api.put(`/followups/${f.id}`, { status: 'completed' }); showSuccess('Marked completed'); fetchData(); } catch { showError('Failed'); } }}
                          className="px-3 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          Mark Completed
                        </button>
                        <button
                          data-testid={`followup-missed-${f.id}`}
                          onClick={async () => { try { await api.put(`/followups/${f.id}`, { status: 'missed' }); showSuccess('Marked missed'); fetchData(); } catch { showError('Failed'); } }}
                          className="px-3 py-1 text-xs rounded-md border border-border hover:bg-muted"
                        >
                          Mark Missed
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Schedule Follow-up</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Lead *</label>
                  <select required value={formData.lead_id} onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                    <option value="">Select a lead...</option>
                    {leads.map((l) => (
                      <option key={l.id} value={l.id}>{l.customer_name} - {l.mobile_number}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Date *</label>
                    <input type="date" required value={formData.follow_up_date} onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Time *</label>
                    <input type="time" required value={formData.follow_up_time} onChange={(e) => setFormData({ ...formData, follow_up_time: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Follow-up Type *</label>
                  <select required value={formData.follow_up_type} onChange={(e) => setFormData({ ...formData, follow_up_type: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none">
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="visit">Visit</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms">SMS</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Remarks *</label>
                  <textarea rows="3" required value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" placeholder="What needs to happen during this follow-up?" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Next Follow-up Date (optional)</label>
                  <input type="date" value={formData.next_follow_up_date} onChange={(e) => setFormData({ ...formData, next_follow_up_date: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">Cancel</button>
                  <button type="submit" disabled={submitting} data-testid="submit-followup" className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    Schedule
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FollowUps;
